import React, { useState, useEffect } from 'react';
import AudioPlayer from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';
import PlaylistModal from './PlaylistModel';
import './Playlists.css';
import { fetchAuthSession } from 'aws-amplify/auth';

// Update the API URL to use the new Music API endpoint
const API_URL = 'https://f146rpvw02.execute-api.us-east-1.amazonaws.com/prod';

const Playlists = () => {
  const [playlists, setPlaylists] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentPlaylist, setCurrentPlaylist] = useState(null);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [showPlaylistModal, setShowPlaylistModal] = useState(false);
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [authToken, setAuthToken] = useState(null);

  useEffect(() => {
    const getAuthToken = async () => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens.idToken.toString();
        setAuthToken(token);
        return token;
      } catch (error) {
        console.error('Error getting auth token:', error);
        setError('Authentication error. Please try logging in again.');
        return null;
      }
    };

    getAuthToken().then(token => {
      if (token) {
        fetchPlaylists(token);
        fetchAudioFiles(token);
      }
    });
  }, []);

  const fetchPlaylists = async (token) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/playlists`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Playlists from API:', data);
      
      // Map the data to match the expected format
      const formattedPlaylists = data.map(playlist => ({
        ...playlist,
        id: playlist.playlistId // Ensure id is set to playlistId for compatibility
      }));
      
      setPlaylists(formattedPlaylists);
      
      // If there are playlists, set the first one as current
      if (formattedPlaylists.length > 0 && !currentPlaylist) {
        setCurrentPlaylist(formattedPlaylists[0]);
      }
    } catch (error) {
      console.error('Error fetching playlists:', error);
      // For now, use mock data if the API isn't implemented yet
      const mockPlaylists = [
        {
          id: '1',
          name: 'My Favorite Tracks',
          description: 'A collection of my favorite Night Bacon tracks',
          tracks: ['01 Ideas/demo-song.mp3', '03 Rehearsal Recordings/jam-session.mp3']
        },
        {
          id: '2',
          name: 'Practice Set',
          description: 'Songs we need to practice',
          tracks: ['02 Ready For Band (agreed)/song1.mp3']
        }
      ];
      setPlaylists(mockPlaylists);
      if (!currentPlaylist) {
        setCurrentPlaylist(mockPlaylists[0]);
      }
      setError(`Failed to load playlists: ${error.message}`);
    }
    setIsLoading(false);
  };

  const fetchAudioFiles = async (token) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/files`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Filter only audio files
      const audioFiles = data.filter(file => {
        const extension = file.key.split('.').pop().toLowerCase();
        return ['mp3', 'wav', 'm4a', 'ogg', 'flac'].includes(extension);
      });
      
      setFiles(audioFiles);
    } catch (error) {
      console.error('Error fetching audio files:', error);
      setError(`Failed to load audio files: ${error.message}`);
    }
    setIsLoading(false);
  };

  const handleCreatePlaylist = () => {
    setEditingPlaylist(null);
    setShowPlaylistModal(true);
  };

  const handleEditPlaylist = (playlist) => {
    setEditingPlaylist(playlist);
    setShowPlaylistModal(true);
  };

  const handleSavePlaylist = async (playlistData) => {
    setIsLoading(true);
    setError(null);
    try {
      // This will need to be implemented in the backend
      const method = playlistData.id ? 'PUT' : 'POST';
      const url = playlistData.id 
        ? `${API_URL}/playlists/${playlistData.id}` 
        : `${API_URL}/playlists`;
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(playlistData)
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      const savedPlaylist = await response.json();
      console.log('Saved playlist:', savedPlaylist);
      
      // Ensure the saved playlist has an id property that matches playlistId
      const formattedPlaylist = {
        ...savedPlaylist,
        id: savedPlaylist.playlistId
      };
      
      // For now, simulate the response
      if (playlistData.id) {
        // Update existing playlist
        setPlaylists(playlists.map(p => 
          p.id === playlistData.id ? formattedPlaylist : p
        ));
        if (currentPlaylist?.id === playlistData.id) {
          setCurrentPlaylist(formattedPlaylist);
        }
      } else {
        // Create new playlist
        setPlaylists([...playlists, formattedPlaylist]);
        if (!currentPlaylist) {
          setCurrentPlaylist(formattedPlaylist);
        }
      }
      
      setShowPlaylistModal(false);
    } catch (error) {
      console.error('Error saving playlist:', error);
      setError(`Failed to save playlist: ${error.message}`);
    }
    setIsLoading(false);
  };

  const handleDeletePlaylist = async (playlistId) => {
    if (!window.confirm('Are you sure you want to delete this playlist?')) {
      return;
    }
    
    setIsLoading(true);
    setError(null);
    try {
      // This will need to be implemented in the backend
      const response = await fetch(`${API_URL}/playlists/${playlistId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      // Remove from state
      const updatedPlaylists = playlists.filter(p => p.id !== playlistId);
      setPlaylists(updatedPlaylists);
      
      // If we deleted the current playlist, set a new one
      if (currentPlaylist?.id === playlistId) {
        setCurrentPlaylist(updatedPlaylists.length > 0 ? updatedPlaylists[0] : null);
        setCurrentTrack(null);
      }
    } catch (error) {
      console.error('Error deleting playlist:', error);
      setError(`Failed to delete playlist: ${error.message}`);
    }
    setIsLoading(false);
  };

  const handleAddToPlaylist = async (playlistId, trackKey) => {
    setIsLoading(true);
    setError(null);
    try {
      console.log(`Adding track ${trackKey} to playlist ${playlistId}`);
      
      if (!playlistId || playlistId === undefined || playlistId === 'undefined') {
        console.error('Invalid playlist ID:', playlistId);
        setError('Cannot add track: Invalid playlist ID');
        setIsLoading(false);
        return;
      }
      
      // This will need to be implemented in the backend
      const response = await fetch(`${API_URL}/playlists/${playlistId}/tracks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ trackKey })
      });
      
      // Update state
      const updatedPlaylists = playlists.map(p => {
        if (p.id === playlistId) {
          return {
            ...p,
            tracks: [...(p.tracks || []), trackKey]
          };
        }
        return p;
      });
      
      setPlaylists(updatedPlaylists);
      
      // Update current playlist if needed
      if (currentPlaylist?.id === playlistId) {
        setCurrentPlaylist({
          ...currentPlaylist,
          tracks: [...(currentPlaylist.tracks || []), trackKey]
        });
      }
    } catch (error) {
      console.error('Error adding track to playlist:', error);
      
      // For now, simulate the response
      const updatedPlaylists = playlists.map(p => {
        if (p.id === playlistId) {
          return {
            ...p,
            tracks: [...(p.tracks || []), trackKey]
          };
        }
        return p;
      });
      
      setPlaylists(updatedPlaylists);
      
      // Update current playlist if needed
      if (currentPlaylist?.id === playlistId) {
        setCurrentPlaylist({
          ...currentPlaylist,
          tracks: [...(currentPlaylist.tracks || []), trackKey]
        });
      }
      
      setError(`Failed to add track to playlist: ${error.message}`);
    }
    setIsLoading(false);
  };

  const handleRemoveFromPlaylist = async (playlistId, trackKey) => {
    setIsLoading(true);
    setError(null);
    try {
      console.log(`Removing track ${trackKey} from playlist ${playlistId}`);
      
      if (!playlistId || playlistId === undefined || playlistId === 'undefined') {
        console.error('Invalid playlist ID:', playlistId);
        setError('Cannot remove track: Invalid playlist ID');
        setIsLoading(false);
        return;
      }
      
      // This will need to be implemented in the backend
      const response = await fetch(`${API_URL}/playlists/${playlistId}/tracks`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ trackKey })
      });
      
      // Update state
      const updatedPlaylists = playlists.map(p => {
        if (p.id === playlistId) {
          return {
            ...p,
            tracks: (p.tracks || []).filter(t => t !== trackKey)
          };
        }
        return p;
      });
      
      setPlaylists(updatedPlaylists);
      
      // Update current playlist if needed
      if (currentPlaylist?.id === playlistId) {
        const updatedPlaylist = {
          ...currentPlaylist,
          tracks: (currentPlaylist.tracks || []).filter(t => t !== trackKey)
        };
        setCurrentPlaylist(updatedPlaylist);
        
        // If we removed the current track, reset it
        if (currentTrack && currentTrack.key === trackKey) {
          setCurrentTrack(null);
        }
      }
    } catch (error) {
      console.error('Error removing track from playlist:', error);
      
      // For now, simulate the response
      const updatedPlaylists = playlists.map(p => {
        if (p.id === playlistId) {
          return {
            ...p,
            tracks: (p.tracks || []).filter(t => t !== trackKey)
          };
        }
        return p;
      });
      
      setPlaylists(updatedPlaylists);
      
      // Update current playlist if needed
      if (currentPlaylist?.id === playlistId) {
        const updatedPlaylist = {
          ...currentPlaylist,
          tracks: (currentPlaylist.tracks || []).filter(t => t !== trackKey)
        };
        setCurrentPlaylist(updatedPlaylist);
        
        // If we removed the current track, reset it
        if (currentTrack && currentTrack.key === trackKey) {
          setCurrentTrack(null);
        }
      }
      
      setError(`Failed to remove track from playlist: ${error.message}`);
    }
    setIsLoading(false);
  };

  const handlePlayTrack = (track) => {
    setCurrentTrack(track);
  };

  const handleSelectPlaylist = (playlist) => {
    console.log('Selected playlist:', playlist);
    setCurrentPlaylist(playlist);
    setCurrentTrack(null);
  };

  const handleEnded = () => {
    if (!currentPlaylist || !currentTrack) return;
    
    const currentTracks = currentPlaylist.tracks || [];
    const currentIndex = currentTracks.findIndex(trackKey => trackKey === currentTrack.key);
    
    if (currentIndex >= 0 && currentIndex < currentTracks.length - 1) {
      // Play next track
      const nextTrackKey = currentTracks[currentIndex + 1];
      const nextTrack = files.find(file => file.key === nextTrackKey);
      if (nextTrack) {
        setCurrentTrack(nextTrack);
      }
    }
  };

  const getPlaylistTracks = (playlist) => {
    if (!playlist || !playlist.tracks) return [];
    
    return playlist.tracks
      .map(trackKey => files.find(file => file.key === trackKey))
      .filter(track => track); // Filter out undefined tracks
  };

  const getAvailableTracks = () => {
    if (!currentPlaylist || !currentPlaylist.tracks) return files;
    
    return files.filter(file => 
      !currentPlaylist.tracks.includes(file.key)
    );
  };

  return (
    <div className="playlists-container">
      <h2>Playlists</h2>
      
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="dismiss-button">Dismiss</button>
        </div>
      )}
      
      {currentTrack && (
        <div className="player-container top-player">
          <div className="now-playing">
            <h3>Now Playing</h3>
            <p>{currentTrack.key.split('/').pop()}</p>
          </div>
          <AudioPlayer
            src={currentTrack.url}
            autoPlay={true}
            showSkipControls={true}
            showJumpControls={true}
            onEnded={handleEnded}
            onClickPrevious={() => {
              if (!currentPlaylist || !currentTrack) return;
              
              const currentTracks = currentPlaylist.tracks || [];
              const currentIndex = currentTracks.findIndex(trackKey => trackKey === currentTrack.key);
              
              if (currentIndex > 0) {
                const prevTrackKey = currentTracks[currentIndex - 1];
                const prevTrack = files.find(file => file.key === prevTrackKey);
                if (prevTrack) {
                  setCurrentTrack(prevTrack);
                }
              }
            }}
            onClickNext={() => {
              if (!currentPlaylist || !currentTrack) return;
              
              const currentTracks = currentPlaylist.tracks || [];
              const currentIndex = currentTracks.findIndex(trackKey => trackKey === currentTrack.key);
              
              if (currentIndex >= 0 && currentIndex < currentTracks.length - 1) {
                const nextTrackKey = currentTracks[currentIndex + 1];
                const nextTrack = files.find(file => file.key === nextTrackKey);
                if (nextTrack) {
                  setCurrentTrack(nextTrack);
                }
              }
            }}
          />
        </div>
      )}
      
      <div className="playlists-layout">
        <div className="playlists-sidebar">
          <div className="playlists-header">
            <h3>Your Playlists</h3>
            <button 
              className="create-playlist-button"
              onClick={handleCreatePlaylist}
            >
              Create Playlist
            </button>
          </div>
          
          {isLoading && playlists.length === 0 ? (
            <div className="loading">Loading playlists...</div>
          ) : (
            <ul className="playlist-list">
              {playlists.length === 0 ? (
                <li className="no-playlists">No playlists yet</li>
              ) : (
                playlists.map(playlist => (
                  <li 
                    key={playlist.id} 
                    className={currentPlaylist?.id === playlist.id ? 'active' : ''}
                    onClick={() => handleSelectPlaylist(playlist)}
                  >
                    <div className="playlist-info">
                      <span className="playlist-name">{playlist.name}</span>
                      <span className="playlist-tracks">{(playlist.tracks || []).length} tracks</span>
                    </div>
                    <div className="playlist-actions">
                      <button 
                        className="edit-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditPlaylist(playlist);
                        }}
                      >
                        Edit
                      </button>
                      <button 
                        className="delete-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeletePlaylist(playlist.id);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
        
        <div className="playlist-content">
          {currentPlaylist ? (
            <>
              <div className="playlist-header">
                <div className="playlist-details">
                  <h3>{currentPlaylist.name}</h3>
                  {currentPlaylist.description && (
                    <p className="playlist-description">{currentPlaylist.description}</p>
                  )}
                  <p className="playlist-count">
                    {(currentPlaylist.tracks || []).length} tracks
                  </p>
                </div>
              </div>
              
              <div className="playlist-tracks">
                <h4>Playlist Tracks</h4>
                {getPlaylistTracks(currentPlaylist).length === 0 ? (
                  <p className="no-tracks">No tracks in this playlist yet</p>
                ) : (
                  <ul className="track-list">
                    {getPlaylistTracks(currentPlaylist).map((track, index) => (
                      <li 
                        key={track.key} 
                        className={currentTrack?.key === track.key ? 'active' : ''}
                      >
                        <div className="track-info" onClick={() => handlePlayTrack(track)}>
                          <span className="track-number">{index + 1}</span>
                          <span className="track-name">{track.key.split('/').pop()}</span>
                        </div>
                        <button 
                          className="remove-track-button"
                          onClick={() => handleRemoveFromPlaylist(currentPlaylist.id, track.key)}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              
              <div className="available-tracks">
                <h4>Available Tracks</h4>
                {getAvailableTracks().length === 0 ? (
                  <p className="no-tracks">No additional tracks available</p>
                ) : (
                  <ul className="track-list">
                    {getAvailableTracks().map((track, index) => (
                      <li key={track.key}>
                        <div className="track-info" onClick={() => handlePlayTrack(track)}>
                          <span className="track-name">{track.key.split('/').pop()}</span>
                        </div>
                        <button 
                          className="add-track-button"
                          onClick={() => handleAddToPlaylist(currentPlaylist.id, track.key)}
                        >
                          Add to Playlist
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          ) : (
            <div className="no-playlist-selected">
              <p>Select a playlist or create a new one</p>
            </div>
          )}
        </div>
      </div>
      
      <PlaylistModal
        isOpen={showPlaylistModal}
        onClose={() => setShowPlaylistModal(false)}
        onSave={handleSavePlaylist}
        playlist={editingPlaylist}
      />
    </div>
  );
};

export default Playlists;
